import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { RiHome5Line } from "react-icons/ri";
import dayjs, { Dayjs } from "dayjs";
import CalendarAdd from "@assets/svg/calendarAdd.svg?react";
import CustomSelect from "@Common/CustomSelect";
import CustomDatePicker from "@Common/CustomDatePicker";
import {
  BookingInformationFormWrapper,
  PaymentInfoContainer,
  PaymentHeader,
  PaymentTitle,
  PaymentRow,
  PaymentLabel,
  PaymentValue,
  TotalPriceLabel,
  TotalPriceValue,
  DirectPaymentContainer,
  DirectPaymentTextContainer,
  DirectPaymentTitle,
  DirectPaymentSubtitle,
} from "./styles";
import { IFormManualReservation } from "..";
import {
  useGetCalenderData,
  useGetListingManagement,
  useGetUserDetail,
  useTemplate,
} from "@services";
import isBetween from "dayjs/plugin/isBetween";
import Money from "@assets/svg/Moneys.svg?react";
import Input from "@Common/input";

import useBreakPoinst from "@hooks/useBreakPoinst";
import Toggle from "../../../components/Toggle";
import { FORM_REGEX_VALIDATORS, propertyHasGuestBook } from "@utils";
import { Background } from "src/utils/image-consts";
import { Box } from "@mui/material";
dayjs.extend(isBetween);
export default function BookingInformationForm() {
  /* ------------------------------- Local State ------------------------------ */
  const [pageCount, setPageCount] = useState(1);
  const [currentMonthDate, setCurrentMonthDate] = useState<string>(
    dayjs().format("YYYY-MM-DD")
  );
  const moveCountRef = useRef(1);
  const prevMonthRef = useRef<Dayjs>(dayjs());
  /* ---------------------------------- Hooks --------------------------------- */
  const { t } = useTranslation();
  const { isPhone } = useBreakPoinst();
  const { watch, setValue, getValues } =
    useFormContext<IFormManualReservation>();
  const propertyIdWatch = watch("booking_information.propertyId");
  const checkInWatch = watch("booking_information.checkIn");
  const checkOutWatch = watch("booking_information.checkOut");
  const search = watch("booking_information.propertyName");
  const isUserPaymentActiveWatch = watch(
    "booking_information.isUserPaymentActive"
  );
  const checkIn = dayjs(checkInWatch);
  const checkOut = dayjs(checkOutWatch);
  const totalDays = checkOut.diff(checkIn, "days") || 0;

  const datePickerStartDate = useMemo(() => {
    const firstDayOfMonth = dayjs(currentMonthDate).startOf('month');
    const dayOfWeek = firstDayOfMonth.day();
    const daysToSubtract = dayOfWeek;
    const startDate = firstDayOfMonth.subtract(daysToSubtract, 'days');
    return startDate.format('YYYY-MM-DD');
  }, [currentMonthDate]);

  /* ---------------------------------- API's --------------------------------- */
  const { data: getListingManagement } = useGetListingManagement();
  const { data: getCalenderData } = useGetCalenderData(
    {
      page: pageCount,
      currentDate: datePickerStartDate, // Send first visible day in date picker grid
      propertyId: propertyIdWatch ? Number(propertyIdWatch) : undefined, // Send selected property ID
    },
    {
      enabled: !!datePickerStartDate && !!propertyIdWatch, // Only fetch when we have both date and property selected
    }
  );
  const { data: userData } = useGetUserDetail();
  const isUserPaymentActive = (userData?.result as any)?.isUserPaymentActive;
  // Direct payment sends the payment link + welcome book (a per-property
  // messaging template / "guest book"), so the toggle only works when the
  // SELECTED property has one assigned — mirroring the backend's per-property
  // check. A host with a guest book on another property must still see it
  // disabled here.
  const { getTemplatesQuery } = useTemplate();
  const { data: templates } = getTemplatesQuery();
  const selectedPropertyHasGuestBook = useMemo(
    () =>
      propertyHasGuestBook(
        templates,
        propertyIdWatch ? Number(propertyIdWatch) : undefined
      ),
    [templates, propertyIdWatch]
  );
  const navigate = useNavigate();

  // Default the direct-payment toggle whenever the selected property (or its
  // guest-book status, once templates load) changes: on for a property that has
  // a guest book and payments enabled, off otherwise. Keyed on that signature —
  // not the toggle value — so a host's manual on/off within the same property is
  // never clobbered.
  const seededToggleRef = useRef<string>("");
  useEffect(() => {
    const signature = `${propertyIdWatch ?? ""}:${selectedPropertyHasGuestBook}`;
    if (seededToggleRef.current === signature) return;
    seededToggleRef.current = signature;
    setValue(
      "booking_information.isUserPaymentActive",
      Boolean(isUserPaymentActive) && selectedPropertyHasGuestBook
    );
  }, [propertyIdWatch, selectedPropertyHasGuestBook, isUserPaymentActive, setValue]);

  /* ----------------------------- Derived Values ----------------------------- */
  const propertyList = useMemo(() => {
    return (
      getListingManagement?.result?.map((item) => ({
        label: item.propertyName,
        value: String(item.propertyId),
      })) ?? []
    );
  }, [getListingManagement]);

  const disabledRanges = useMemo(() => {
    const unavailable = getCalenderData?.result.flatMap((item) =>
      item.dates.filter((d) => d.availability === "unavailable")
    );

    return (
      unavailable?.map(({ date }) => ({
        from: date,
        to: date,
      })) ?? []
    );
  }, [getCalenderData]);

  /* ----------------------------- Helper Methods ----------------------------- */
  const shouldDisableDate = useCallback((date: Dayjs) =>
    disabledRanges.some((range) =>
      date.isBetween(dayjs(range.from), dayjs(range.to), "day", "[]")
    ), [disabledRanges]);

  // Helper function to disable dates for check-out (same day and before check-in)
  const shouldDisableCheckOutDate = useCallback((date: Dayjs) => {
    if (!checkInWatch) return false;
    
    // Disable if date is same as or before check-in date
    const isBeforeOrSameAsCheckIn = date.isSameOrBefore(dayjs(checkInWatch), 'day');
    
    // Also check for unavailable dates
    const isUnavailable = shouldDisableDate(date);
    
    return isBeforeOrSameAsCheckIn || isUnavailable;
  }, [checkInWatch, shouldDisableDate]);

  const requiredMessage = useCallback((messageKey: string) => ({
    required: {
      value: true,
      message: t(messageKey),
    },
  }), [t]);

  // Custom validation for check-out date
  const checkOutValidation = useMemo(() => ({
    required: {
      value: true,
      message: t("MANUALRESERVATION.PLEASESELECTTHEENDDATEOFTHERESERVATION"),
    },
    validate: {
      afterCheckIn: (value: string) => {
        if (!checkInWatch || !value) return true;
        const checkInDate = dayjs(checkInWatch);
        const checkOutDate = dayjs(value);
        
        if (checkOutDate.isSameOrBefore(checkInDate, 'day')) {
          return t("MANUALRESERVATION.CHECKOUT_MUST_BE_AFTER_CHECKIN") || "Check-out date must be after check-in date";
        }
        return true;
      },
    },
  }), [checkInWatch, t]);

  // Memoize minDate calculation for check-out date picker
  const checkOutMinDate = useMemo(() => {
    return checkInWatch ? dayjs(checkInWatch).add(1, 'day') : undefined;
  }, [checkInWatch]);

  /* --------------------------------- Effects -------------------------------- */

  useEffect(() => {
    if (!checkInWatch) return;
    
    const checkOutCurrent = getValues("booking_information.checkOut");
    const checkInDate = dayjs(checkInWatch);
    const nextDay = checkInDate.add(1, "day").format("YYYY-MM-DD");

    // If no check-out date or check-out is same/before check-in, set to next day
    if (
      !checkOutCurrent || 
      dayjs(checkOutCurrent).isSameOrBefore(checkInDate, 'day')
    ) {
      setValue("booking_information.checkOut", nextDay);
    }
  }, [checkInWatch]);

  useEffect(() => {
    if (propertyIdWatch) {
      const propertyName = propertyList?.filter(
        (item: any) => item.value === propertyIdWatch
      )[0].label;
      setValue("booking_information.propertyName", propertyName);
    } else {
      setValue("booking_information.propertyName", "");
    }
  }, [propertyIdWatch, propertyList]);


  // The toggle's initial value is seeded once by the parent (ManualReservation),
  // which stays mounted for the whole flow. Seeding it here too would reset the
  // host's choice every time they step back to this tab.
  return (
    <BookingInformationFormWrapper>
      <div className="containerItem">
        <div className="containerSVG">
          <RiHome5Line />
        </div>
        <p>{t("MANUALRESERVATION.PROPERTYINFORMATION")}</p>
        <CustomSelect
          data={propertyList}
          name="booking_information.propertyId"
          label={t("COMMON.PROPERTY")}
          registerOptions={requiredMessage(
            "MANUALRESERVATION.PLEASESELECTTHEPROPERTYFROMTHEDROPDOWNLIST"
          )}
        />
      </div>

      <div className="containerItem">
        <div className="containerSVG">
          <CalendarAdd />
        </div>
        <p>{t("MANUALRESERVATION.TIMEANDDATEOFRESERVATION")}</p>
        <CustomDatePicker
          views={["day"]}
          disablePast
          name="booking_information.checkIn"
          label={t("MANUALRESERVATION.BOOKINGSTARTDATE")}
          format="YYYY-MM-DD"
          disabled={!propertyIdWatch}
          shouldDisableDate={shouldDisableDate}
          registerOptions={requiredMessage(
            "MANUALRESERVATION.PLEASESELECTTHESTARTDATEOFTHERESERVATION"
          )}
          onMonthChange={(date) => {
            const isNext = dayjs(date).isAfter(prevMonthRef.current, "month");
            const isPrev = dayjs(date).isBefore(prevMonthRef.current, "month");
            if (isNext) {
              moveCountRef.current += 1;
              setPageCount(moveCountRef.current);
            } else if (isPrev) {
              moveCountRef.current -= 1;
              setPageCount(moveCountRef.current);
            }
            prevMonthRef.current = date;
            // Update current month date to trigger API refetch for new month
            setCurrentMonthDate(dayjs(date).format("YYYY-MM-DD"));
          }}
        />
        <CustomDatePicker
          views={["day"]}
          disablePast
          name="booking_information.checkOut"
          label={t("MANUALRESERVATION.BOOKINGENDDATE")}
          format="YYYY-MM-DD"
          disabled={!propertyIdWatch || !checkInWatch}
          shouldDisableDate={shouldDisableCheckOutDate}
          minDate={checkOutMinDate}
          registerOptions={checkOutValidation}
          onMonthChange={(date) => {
            const isNext = dayjs(date).isAfter(prevMonthRef.current, "month");
            const isPrev = dayjs(date).isBefore(prevMonthRef.current, "month");
            if (isNext) {
              moveCountRef.current += 1;
              setPageCount(moveCountRef.current);
            } else if (isPrev) {
              moveCountRef.current -= 1;
              setPageCount(moveCountRef.current);
            }
            prevMonthRef.current = date;
            // Update current month date to trigger API refetch for new month
            setCurrentMonthDate(dayjs(date).format("YYYY-MM-DD"));
          }}
        />
      </div>

      <PaymentInfoContainer $isPhone={isPhone}>
        <PaymentHeader $isPhone={isPhone}>
          <Money />
          <PaymentTitle $isPhone={isPhone}>
            {t("MANUALRESERVATION.PAYMENT_INFO")}
          </PaymentTitle>
        </PaymentHeader>
        <PaymentRow $isPhone={isPhone}>
          <PaymentLabel $isPhone={isPhone}>
            {t("MANUALRESERVATION.DURATION")}
          </PaymentLabel>
          <PaymentValue $isPhone={isPhone}>
            {`${totalDays} ${t("MANUALRESERVATION.NIGHTS_COUNT")}`}
          </PaymentValue>
        </PaymentRow>
        <PaymentRow $isPhone={isPhone}>
          <TotalPriceLabel $isPhone={isPhone}>
            {t("MANUALRESERVATION.TOTAL_BOOKING_PRICE")}
          </TotalPriceLabel>
          {/* <TotalPriceValue $isPhone={isPhone}>
            
            {t("MANUALRESERVATION.PRICE_AMOUNT")}
          </TotalPriceValue> */}
          <Input
            suffixLabel={t("MANUALRESERVATION.PRICE_AMOUNT")}
            style={{
              width: "140px",
              height: "10px",

              marginTop: "-35px",
            }}
            name="booking_information.nightPrice"
            label=""
            placeholder=""
            registerOptions={{
              required: {
                value: true,
                message: t("MANUALRESERVATION.PLEASEENTERTHENIGHTRATE"),
              },
              pattern: {
                value: FORM_REGEX_VALIDATORS.digitsDecimal,
                message: t(
                  "MANUALRESERVATION.ONLYNUMBERSAREALLOWEDINTHISFIELD"
                ),
              },
            }}
          />
        </PaymentRow>
      </PaymentInfoContainer>
      {isUserPaymentActive ? (
        <>
          <DirectPaymentContainer $isPhone={isPhone}>
            <DirectPaymentTextContainer $isPhone={isPhone}>
              <DirectPaymentTitle $isPhone={isPhone}>
                {t("MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT")}
              </DirectPaymentTitle>
              <DirectPaymentSubtitle $isPhone={isPhone}>
                {t("MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION")}
              </DirectPaymentSubtitle>
            </DirectPaymentTextContainer>
            {/* Keep the toggle visible even without a welcome-book template, but
                disable it — the host sees the option and (via the alert below)
                the reason it can't be turned on yet. */}
            <Toggle
              disabled={!selectedPropertyHasGuestBook}
              checked={
                selectedPropertyHasGuestBook ? isUserPaymentActiveWatch : false
              }
              onChange={(event, checked) => {
                if (!selectedPropertyHasGuestBook) return;
                setValue("booking_information.isUserPaymentActive", checked);
              }}
            />
          </DirectPaymentContainer>
          {propertyIdWatch && !selectedPropertyHasGuestBook && (
            <>
              <Box height={12} />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isPhone ? "column" : "row",
                  alignItems: isPhone ? "stretch" : "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  border: "1px solid #FCD34D",
                  backgroundColor: "#FFFBEB",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <Box sx={{ fontSize: "13px", color: "#92803F", lineHeight: "20px" }}>
                  {t("MANUALRESERVATION.NO_TEMPLATE_ALERT")}
                </Box>
                <Box
                  component="button"
                  type="button"
                  onClick={() => navigate("/messaging")}
                  sx={{
                    whiteSpace: "nowrap",
                    backgroundColor: "#5B3FFF",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "13px",
                    borderRadius: "8px",
                    padding: "10px 18px",
                    border: "none",
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#4930CC" },
                  }}
                >
                  {t("MANUALRESERVATION.CREATE_TEMPLATE")}
                </Box>
              </Box>
            </>
          )}
        </>
      ) : null}
    </BookingInformationFormWrapper>
  );
}
